import {
  Avatar,
  Divider,
  Grid,
  Link,
  makeStyles,
  Menu,
  MenuItem,
  Typography
} from "@material-ui/core";
import PropTypes from "prop-types";
import React from "react";
import { Permissions } from '../../../data/Permissions';
import * as Constants from "../../../data/StringConstants";
import LibraryAwareTheme from "../../../layouts/Main/components/LibraryAwareTheme/LibraryAwareTheme";
import customTheme from '../../../theme/customTheme/customTheme';

/**
 * @description - Profile Menu for LA site navigation
 * @param {number} orgId - LA.OrganizationId The Organization Id
 * @param {object} user - LA.User object
 * @param {object} userPermissions - LA.Security object
 * @todo - eventually standardize this via AppContext
 */
export default function ProfileMenu({ orgId, user, userPermissions }) {
	/**
	 * @description - use of `!important` is required here in order to override Link's default styling
	 * @todo Add the removal of text-decoration from Link component to our theming
	 */
  const classes = makeStyles({
    avatar: {
      backgroundColor: customTheme.palette.primary.main,
      color: "white",
      width: "2.75ex",
      height: "2.75ex",
      cursor: "pointer",
    },
    initials: {
      fontSize: customTheme.typography.body2.fontSize,
    },
    profileItem: {
      color: customTheme.palette.grey.main,
      textTransform: "uppercase",
      textAlign: "center",
      justifyContent: "center",
      "&:hover": {
        backgroundColor: customTheme.palette.primary.main,
        color: '#fff'
      },
    },
    logoutMenuItem: {
      color: customTheme.palette.error.main,
      textDecoration: "none !important",
      textAlign: "center",
      justifyContent: "center",
      textTransform: "uppercase",
      "&:hover": {
        backgroundColor: customTheme.palette.error.main,
        color: customTheme.palette.error.contrastText,
      },
    },
    removeTextDecoration: {
      textDecoration: "none !important",
    }
  })();

  const [anchorEl, setAnchorEl] = React.useState(null);

  const toggleMenu = event => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);
	/**
	 * @description Any of these roles are provided access to the Support Dashboard
	 */
  const supportDashboardRoles = [Permissions.globalAdmin, Permissions.globalEditor, Permissions.globalSupport];

	/**
	 * @description coalesce a boolean from the userPermission collection
	 */
  const enableSupportDashboard = !!supportDashboardRoles.filter(role => userPermissions[role]).length;

  /**
   * @description - Used as the "avatar" treatment.
   * @todo - future enhancement will see profile pictures manifesting in place of this when available
   */
  const initials = `${user.FirstName.charAt()}`

  // We need to construct the complete URI for the menu items since the ProfileMenu can exist 
  // in parts of the app (ex: Legacy Editor) which are not running the Backbone application. 
  const profileSettingsUrl = `/${orgId}/${Constants.ROUTE_URIS.PROFILE_SETTINGS}`;
  const supportDashboardUrl = `/${orgId}/${Constants.ROUTE_URIS.SUPPORT}`;

  /**
   * @todo - In the future, we will want to utilize `props.children` and abstract the `Menu` 's inner
   * members to a more suitable place. Circa March 2020, we explicitly set the Menu's inner membership
   * here because of the organization of frontend code prevents performing this elsewhere at time of
   * US593629.
   */
  return (
    <LibraryAwareTheme>
      <Grid container justify="center">
        <Grid item>
          <Avatar className={classes.avatar}
            aria-controls="nav-profile-menu"
            aria-haspopup="true"
            onClick={toggleMenu}
          >
            <Typography className={classes.initials}>{initials}</Typography>
          </Avatar>
          <Menu
            id="nav-profile-menu"
            getContentAnchorEl={null}
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Link
              onClick={handleClose}
              id="profile-settings"
              alt={Constants.SITE_CONTROLS.PROFILE.SETTINGS.ALT}
              className={classes.removeTextDecoration}
              href={profileSettingsUrl}
            >
              <MenuItem className={classes.profileItem}>
                {Constants.SITE_CONTROLS.PROFILE.SETTINGS.LABEL}
              </MenuItem>
            </Link>
            {
              enableSupportDashboard &&
              <Link
                onClick={handleClose}
                id="support-dashboard"
                alt={Constants.SITE_CONTROLS.PROFILE.SUPPORT_DASHBOARD.ALT}
                className={classes.removeTextDecoration}
                href={supportDashboardUrl}>
                <MenuItem className={classes.profileItem}>
                  {Constants.SITE_CONTROLS.PROFILE.SUPPORT_DASHBOARD.LABEL}
                </MenuItem>
              </Link>
            }
            <Divider />
            <Link
              onClick={handleClose}
              className={classes.removeTextDecoration}
              href={Constants.ROUTE_URIS.LOGOUT}
              alt={Constants.SITE_CONTROLS.LOGOUT.ALT}
            >
              <MenuItem className={classes.logoutMenuItem}>
                {Constants.SITE_CONTROLS.LOGOUT.LABEL}
              </MenuItem>
            </Link>
          </Menu>
        </Grid>
      </Grid>
    </LibraryAwareTheme>
  );
}

ProfileMenu.propTypes = {
  /**
   * @description - minimum shape requirement
   */
  user: PropTypes.shape({
    FirstName: PropTypes.string,
    LastName: PropTypes.string
  }),
  orgId: PropTypes.number.isRequired
};